import React, { useEffect, useState } from 'react';
import ContentCard from "./ContentCard";
import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import { DragEndEvent } from "@dnd-kit/core/dist/types";
import { arrayMove, rectSortingStrategy, SortableContext, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useNavigate, useParams } from "react-router-dom";
import { Content, Group } from "../typings";
import EditableInput from "../components/EditableInput";
import { Row } from "../components/Flex";
import CreateDialog from "./CreateDialog";
import { arraysEqual } from "../components/utils";
import {
    addToGroup, createInGroup,
    deleteGroup,
    fetchAllContent,
    fetchGroup,
    updateGroupName,
    updateGroupOrder
} from "../content-api";
import { FaAnglesRight } from "react-icons/fa6";
import { useApi } from "../api-context";
import { toast } from "react-toastify";
import { useAuth } from "../auth-context";
import ConfirmationDialog from "../tags/ConfirmationDialog";
import { deletePackage } from "../package-api";

function ContentPage() {
    const { data } = useAuth();
    const api = useApi();
    const [group, setGroup] = useState<Group>({
        name: '',
        path: [],
        contentList: []
    });
    const [order, setOrder] = useState<number[]>();
    const [orderChanged, setOrderChanged] = useState<boolean>();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [contents, setContents] = useState<Content[]>([]);
    const [deleteItem, setDeleteItem] = useState<Content>();

    const navigate = useNavigate();

    let { id } = useParams();

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const loadContent = (id: any) => {
        fetchGroup(api, id)
            .then((result: Group) => {
                setGroup(result);
                setOrder(result.contentList.map(item => item.id));
                loadAddables(result);
            })
            .catch(console.error);
    };

    const loadAddables = (result: Group) => {
        fetchAllContent(api)
            .then(results => {
                const path = result.path.map(i => i.id);
                path.push(Number(id));
                setContents(results.filter(item =>
                    item.parentGroupId !== Number(id) &&
                    (item.type === 'PACKAGE' || !path.includes(item.id))
                ));
            })
            .catch(console.error);
    };

    useEffect(() => {
        loadContent(id);
    }, [id]);

    useEffect(() => {
        setOrderChanged(!arraysEqual(order || [], group.contentList.map(i => i.id)));
    }, [order, group]);

    const onUpdateName = (name: string) => updateGroupName(api, id!, name);
    const onUpdateOrder = () => updateGroupOrder(api, id!, group.contentList)
        .then(() => toast.success('Sorted successfully'))
        .catch(() => toast.error('Sorting failed'));

    const onRemoveContent = (content: Content) => {
        addToGroup(api, "0", content)
            .then(() => loadContent(id));
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            const oldIndex = group.contentList.findIndex(v => v.id === active?.id);
            const newIndex = group.contentList.findIndex(v => v.id === over?.id);
            const contentList = arrayMove(group.contentList, oldIndex, newIndex);
            setGroup({ ...group, contentList });
        }
    };

    return (
        <>
            <div className="group-page-header">
                <Row gap={8} style={{ marginBottom: '8px' }}>
                <div className="main-title" style={{ marginBottom: '8px' }}>
                    {id === "0" ? 'Edit root' : 'Edit group'}
                </div>
                    <div style={{ flex: 1 }}>
                        {
                            id !== "0" && (
                                <EditableInput
                                    value={group.name}
                                    setValue={name => {
                                        onUpdateName(name).then(() => setGroup({ ...group, name }));
                                    }}
                                />
                            )
                        }
                    </div>
                    <button
                        style={{ flex: 0, minWidth: '150px', fontSize: '24px' }}
                        className="my-button"
                        onClick={() => setDialogOpen(true)}
                    >
                        +
                    </button>
                    <button
                        disabled={!orderChanged}
                        style={{ flex: 0, minWidth: '150px' }}
                        className="my-button"
                        onClick={onUpdateOrder}
                    >
                        SAVE ORDER
                    </button>
                    {
                        id !== "0" ? (
                            <button
                                style={{ flex: 0, minWidth: '150px' }}
                                className="my-button"
                                onClick={() => setDeleteItem({
                                    id: Number(id),
                                    type: 'GROUP'
                                } as Content)}
                            >
                                DELETE GROUP
                            </button>
                        ) : <></>
                    }
                </Row>
                <Row gap={8} style={{ marginBottom: '8px' }}>
                    <div>Path</div>
                    <FaAnglesRight />
                    {id === "0" ? <div>{data?.organisationName?.toUpperCase()}</div> : <></>}
                    <>

                        {group.path.reverse().map((part, i) => {
                            if (i === group.path.length - 1) {
                                return (
                                    <a href={"/group/" + part.id} style={{fontWeight: 900}}>{part.name || data?.organisationName?.toUpperCase()}</a>
                                );
                            }
                            return (
                                <>
                                    <a href={"/group/" + part.id} style={{fontWeight: 900}}>{part.name || data?.organisationName?.toUpperCase()}</a>
                                    <div>/</div>
                                </>
                            );
                        })}
                        {id === "0" ? <></> : <div>/ {group.name}</div>}
                    </>
                </Row>
            </div>
            <div className="page-container">
                {
                    id === "0" && (
                        <div className="main-title">
                            Package list
                        </div>
                    )
                }
                <CreateDialog
                    contents={contents.filter(c => c.parentGroupId !== id)}
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onAdd={(contentId, type) => {
                        addToGroup(api, id!, { id: contentId, type } as Content)
                            .then(() => {
                                loadContent(id);
                                setDialogOpen(false);
                                toast.success('Added successfully');
                            })
                            .catch(() => toast.error("Failed to add", ))
                            .finally(() => setDialogOpen(false));
                    }}
                    onCreate={(name, type) => {
                        createInGroup(api, id!, { name, type } as Content)
                            .then(() => {
                                loadContent(id);
                                setDialogOpen(false);
                                toast.success('Created successfully');
                            })
                            .catch(() => toast.error("Failed to create"))
                            .finally(() => setDialogOpen(false));

                    }}
                />
                <div className="content-container">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext items={group.contentList} strategy={rectSortingStrategy}>
                            {group.contentList.map((item) => (
                                <ContentCard
                                    key={item.id}
                                    content={item}
                                    onDelete={() => setDeleteItem(item)}
                                    onRemove={() => onRemoveContent(item)}
                                    hasRemoveFromGroup={id !== "0"}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
            <ConfirmationDialog
                title={"Delete " + (deleteItem?.type === 'PACKAGE' ? 'package' : 'group')}
                description={"Are you sure you want to delete this " + (deleteItem?.type === 'PACKAGE' ? 'package?' : 'group?')}
                open={!!deleteItem}
                onClose={() => setDeleteItem(undefined)}
                onConfirm={() => {
                    if (deleteItem?.type === 'GROUP') {
                        deleteGroup(api, deleteItem.id.toString())
                            .then(() => {
                                toast.success('Deleted successfully');
                                if (deleteItem.id === Number(id)) {
                                    navigate('/group/0');
                                    return;
                                }
                                loadContent(id);
                            })
                            .catch(() => toast.error("Failed to delete"));
                    } else if(deleteItem?.type === 'PACKAGE') {
                        deletePackage(api, deleteItem.id.toString())
                            .then(() => {
                                toast.success('Deleted successfully');
                                loadContent(id);
                            })
                            .catch(() => toast.error("Failed to delete"));
                    }
                    setDeleteItem(undefined);
                }}
            />
        </>
    );
}

export default ContentPage;
